import { Button, Chip, Spinner } from '@sjuni/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/components/FormError';
import { ApiRequestError, apiFetch } from '@/lib/api/client';

import { type RoleRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.10 — `/roles`.
 *
 * "Role editor showing the permission matrix as checkboxes grouped by resource,
 * with the five seeded roles editable but not deletable."
 *
 * One role at a time rather than a grid of five columns by seventy rows. §3's
 * table reads that way on paper because it is a reference; as a screen it is
 * three hundred and fifty checkboxes, and the question anyone actually arrives
 * with is "what can a Document Manager do", which is one column.
 *
 * The grouping comes from the server (`GET /admin/roles/permissions`) — see the
 * note on `RoleController::permissions()` for why the client does not split
 * `news.manage-taxonomy` on a dot.
 */
export function RolesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<number | null>(null);
  const [draft, setDraft] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const roles = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => apiFetch<{ data: RoleRow[] }>('/admin/roles'),
  });

  const groups = useQuery({
    queryKey: ['admin', 'roles', 'permissions'],
    queryFn: () => apiFetch<{ data: Record<string, string[]> }>('/admin/roles/permissions'),
  });

  const list = roles.data?.data ?? [];
  const role = list.find((option) => option.id === selected) ?? list[0] ?? null;
  const held = draft ?? role?.permissions ?? [];

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/admin/roles/${role?.id}`, {
        method: 'PUT',
        body: { name: role?.name, permissions: held },
      }),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
    onError: (cause: unknown) =>
      setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic')),
  });

  const toggle = (permission: string) =>
    setDraft(
      held.includes(permission)
        ? held.filter((name) => name !== permission)
        : [...held, permission],
    );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.roles')}</h1>
        <p className="mt-1 max-w-prose text-body-sm text-secondary">{t('roleEditor.intro')}</p>
      </div>

      <FormError message={error} />

      {roles.isPending || groups.isPending ? (
        <Spinner size="lg" label={t('common.loading')} />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
        <nav aria-label={t('nav.items.roles')}>
          <ul className="space-y-1">
            {list.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  aria-current={role?.id === option.id ? 'true' : undefined}
                  onClick={() => {
                    setSelected(option.id);
                    setDraft(null);
                    setError(null);
                  }}
                  className={[
                    'w-full rounded-card px-3 py-2 text-start text-body-sm transition-colors',
                    role?.id === option.id
                      ? 'bg-navy-600 text-white'
                      : 'hover:bg-surface-subtle text-secondary hover:text-primary',
                  ].join(' ')}
                >
                  <span className="font-semibold">{t(`roles.${option.name}`, option.name)}</span>
                  {/* §5.10's list wants to say how many people hold this
                      before anyone changes what it means. */}
                  <span
                    className={[
                      'mt-0.5 block text-caption',
                      role?.id === option.id ? 'text-white/80' : 'text-muted',
                    ].join(' ')}
                  >
                    {t('roleEditor.heldBy', { count: option.users_count ?? 0 })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {role !== null ? (
          <section aria-labelledby="role-heading" className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 id="role-heading" className="text-h4 font-extrabold text-primary">
                  {t(`roles.${role.name}`, role.name)}
                </h2>
                <p className="mt-1 text-caption text-muted">
                  {t(`users.roleHints.${role.name}`, '')}
                </p>
              </div>

              {role.is_seeded ? <Chip tone="neutral">{t('roleEditor.builtIn')}</Chip> : null}
            </div>

            {/*
              `super-admin` is not editable by anybody. Its permission set is
              not what grants it access — the policy answers on the role — so a
              screen full of live checkboxes here would appear to do something
              and would not.
            */}
            {role.is_editable ? null : (
              <p className="bg-surface-subtle flex items-start gap-2 rounded-card p-3 text-body-sm text-secondary">
                <Lock size={16} strokeWidth={1.75} aria-hidden="true" className="mt-0.5 shrink-0" />
                {t('roleEditor.superAdminLocked')}
              </p>
            )}

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Object.entries(groups.data?.data ?? {}).map(([resource, actions]) => (
                <fieldset key={resource} className="rounded-card border border-subtle p-3">
                  <legend className="px-1 text-body-sm font-extrabold text-primary">
                    {t(`roleEditor.resources.${resource}`, resource)}
                  </legend>

                  <div className="space-y-1">
                    {actions.map((action) => {
                      const permission = `${resource}.${action}`;

                      return (
                        <label
                          key={permission}
                          className="flex items-center gap-2 text-body-sm text-secondary"
                        >
                          <input
                            type="checkbox"
                            className="size-4 rounded border-subtle text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
                            // Checked and disabled for `super-admin`: it holds
                            // everything, and showing it as empty would be a
                            // lie about what the role can do.
                            checked={!role.is_editable || held.includes(permission)}
                            disabled={!role.is_editable}
                            onChange={() => toggle(permission)}
                          />
                          {t(`roleEditor.actions.${action}`, action)}
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              ))}
            </div>

            {role.is_editable ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  disabled={save.isPending || draft === null}
                  onClick={() => save.mutate()}
                >
                  {save.isPending ? t('common.saving') : t('common.save')}
                </Button>

                {draft !== null ? (
                  <Button type="button" variant="ghost" onClick={() => setDraft(null)}>
                    {t('common.cancel')}
                  </Button>
                ) : null}

                <p className="text-caption text-muted">{t('roleEditor.saveHint')}</p>
              </div>
            ) : null}
          </section>
        ) : null}
      </div>
    </div>
  );
}
